import { useMemo } from 'react';
import type { BookingRow, CampaignGroupFields } from '@/models';

/** A campaign row: the shared fields, summed money, and its line items. */
export interface CampaignGroup extends BookingRow {
  groupKey: string;
  lineItems: BookingRow[];
  lineItemCount: number;
}

/**
 * Totals a numeric column across a campaign's lines.
 *
 * Returns null when no line carries a value, so a column the backend left
 * empty stays empty on the grouped row. Summing to 0 instead would turn
 * "not recorded" into a stated zero — the Fee column, which is null on every
 * line that is not a tech fee, would read as a genuine £0.00 charge.
 */
const sum = (rows: BookingRow[], field: keyof BookingRow): number | null => {
  const values = rows
    .map((row) => row[field])
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return values.length ? values.reduce((total, value) => total + value, 0) : null;
};

/** Earliest start and latest end, so a collapsed row spans its whole flight. */
const earliest = (rows: BookingRow[], field: 'start_date' | 'won_date'): string | null => {
  const values = rows.map((row) => row[field]).filter(Boolean) as string[];
  return values.length ? values.sort()[0] : null;
};

const latest = (rows: BookingRow[]): string | null => {
  const values = rows.map((row) => row.end_date).filter(Boolean) as string[];
  return values.length ? values.sort()[values.length - 1] : null;
};

/**
 * Adapts rows into the campaign shape the table renders.
 *
 * The API now groups and pages over campaigns, so a row usually arrives
 * already collapsed and this only has to name it. The local grouping below
 * remains for ungrouped rows -- it is what the expandable line-item list is
 * built from when a caller has them.
 */
/**
 * The span of line-item letters a campaign covers, e.g. `A–D`.
 *
 * Sorted by length first so AA follows Z rather than preceding B — the letters
 * are a base-26 sequence, not plain strings, and sorting them as text would
 * put AA between A and B.
 */
const lineItemRange = (lineItems: BookingRow[]): string | undefined => {
  const letters = lineItems
    .map((item) => item.line_item_id)
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => a.length - b.length || a.localeCompare(b));

  if (!letters.length) return undefined;
  const [first] = letters;
  const last = letters[letters.length - 1];
  return first === last ? first : `${first}–${last}`;
};

export const useCampaignGroups = (rows: BookingRow[]): CampaignGroup[] =>
  useMemo(() => {
    const byCampaign = new Map<string, BookingRow[]>();

    rows.forEach((row) => {
      // Campaign name alone would merge same-named campaigns across advertisers.
      const key = `${row.advertiser ?? ''}::${row.campaign ?? ''}`;
      const existing = byCampaign.get(key);
      if (existing) existing.push(row);
      else byCampaign.set(key, [row]);
    });

    return Array.from(byCampaign.entries()).map(([groupKey, lineItems]) => {
      const [first] = lineItems;

      return {
        ...first,
        groupKey: (first as BookingRow & CampaignGroupFields).group_key ?? groupKey,
        lineItems,
        /* The server's count when the rows arrived grouped -- the local
           `lineItems.length` is 1 there, because the campaign's lines were
           collapsed before they were sent. */
        lineItemCount:
          (first as BookingRow & CampaignGroupFields).line_item_count ?? lineItems.length,
        /* The group spreads its first line item, so this column showed that
           line's letter as though it were the campaign's — "A" on a campaign
           of four lines. A range says what the group actually covers; a
           campaign of one keeps its single letter. */
        line_item_id: lineItemRange(lineItems),
        start_date: earliest(lineItems, 'start_date'),
        end_date: latest(lineItems),
        won_date: earliest(lineItems, 'won_date'),
        gross: sum(lineItems, 'gross'),
        discount: sum(lineItems, 'discount'),
        net: sum(lineItems, 'net'),
        media_spend: sum(lineItems, 'media_spend'),
        fee: sum(lineItems, 'fee'),
        quantity: sum(lineItems, 'quantity')
      } as CampaignGroup;
    });
  }, [rows]);
